import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContentWrapper, RichContent } from "@insite/content-library/Widgets/Basic/RichContent";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FaqAccordion from "@SupplyLand/Widgets/Common/FaqAccordion";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

// added FaqRichContent component that wraps the RichContent component
// and passes only the necessary properties to allow hyperlinks in FAQ answer
interface FaqRichContentProps {
    content: string;
}

const FaqRichContent: React.FC<FaqRichContentProps> = ({ content }) => {
    const styles = {
        wrapper: {},
    };

    return (
        <StyledWrapper>
            <ContentWrapper className="wrapper" {...styles.wrapper} backgroundColor="" padding={0}>
                {parse(content, parserOptions)}
            </ContentWrapper>
        </StyledWrapper>
    );
};

const enum fields {
    question = "question",
    answer = "answer",
    cardBackgroundColor = "cardBackgroundColor",
}
interface Props extends WidgetProps {
    fields: {
        [fields.question]: string;
        [fields.answer]: string;
        [fields.cardBackgroundColor]: string;
    };
}

const defaultCardBackgroundColor = "#e4e1e1";

export interface FaqStyles {
    faqGridItem?: GridItemProps;
    faqAccordion?: AccordionPresentationProps;
    faqQuestion?: TypographyPresentationProps;
    faqAnswer?: TypographyPresentationProps;
    faqGridContainer?: GridContainerProps;
}

export const faqStyles: FaqStyles = {
    faqGridContainer: {
        offsetCss: css`
            justify-content: center;
        `,
    },
    faqGridItem: {
        width: [12, 12, 8, 8, 8],
    },

    faqAccordion: {
        css: css`
            width: 100%;
            background: #e4e1e1;
            box-shadow: 0px 3px 6px #00000029;
            border-radius: 10px;
            padding: 15px;
            margin-bottom: 15px;
            text-align: center;

            /* the mobius accordion header paints its own dt/button backgrounds —
               make them transparent so the card background color shows through */
            dt {
                background: transparent;
            }

            button {
                border: none !important;
                text-align: center;
                background: transparent;
            }
        `,
    },
    faqQuestion: {
        color: "text.main",
        css: css`
            font-weight: 700;
            text-transform: uppercase;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            font-size: 15px;
            padding: 0 60px;

            /* sm and up */
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: 18px;
                        padding: 0 100px;
                    `,
                    css`
                        font-size: 18px;
                        padding: 0 100px;
                    `,
                    css`
                        font-size: 18px;
                        padding: 0 100px;
                    `,
                ])}
        `,
    },
    faqAnswer: {
        css: css`
            border: none;
        `,
    },
};

const styles = faqStyles;

const Faq: React.FC<Props> = props => {
    const cardBackgroundColor = props.fields.cardBackgroundColor || defaultCardBackgroundColor;
    const faqAccordionProps: AccordionPresentationProps = {
        ...styles.faqAccordion,
        css: css`
            ${styles.faqAccordion?.css}
            background: ${cardBackgroundColor};
        `,
    };
    const questionHtml = (
        <Typography {...styles.faqQuestion} itemProp="name">
            {props.fields.question}
        </Typography>
    );
    return (
        <GridContainer {...styles.faqGridContainer}>
            <GridItem {...styles.faqGridItem}>
                <Accordion
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                    headingLevel={2}
                    {...faqAccordionProps}
                >
                    <FaqAccordion initialExpanded={false} title={questionHtml}>
                        <Typography itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                            <FaqRichContent content={props.fields.answer} />
                        </Typography>
                    </FaqAccordion>
                </Accordion>
            </GridItem>
        </GridContainer>
    );
};

const definition: WidgetDefinition = {
    fieldDefinitions: [
        {
            name: fields.question,
            displayName: "Question",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "FAQ Question",
            placeholder: "FAQ Question",
            isRequired: true,
        },
        {
            name: fields.answer,
            displayName: "Answer",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "FAQ Answer",
            placeholder: "FAQ Answer",
            isRequired: true,
        },
        {
            name: fields.cardBackgroundColor,
            displayName: "Card Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: defaultCardBackgroundColor,
            fieldType: "General",
            tooltip: "Background color of the FAQ card. Use SupplyLand yellow (#FFD100) on the Industries pages.",
        },
    ],
    group: "Common",
    displayName: "FAQ",
};

const widgetModule: WidgetModule = {
    component: Faq,
    definition,
};
export default widgetModule;
